import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Credly|Management|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ShareCredlyBadging,
	portal: 'SP',
};

type LocNameStart = 'SPCredlyManagement';
export type SPCredlyManagementLocNames =
	| `${LocNameStart}TitlePageSection`
	| `${LocNameStart}NotificationDeleteSuccess`
	| `${LocNameStart}NotificationDeleteFailure`
	| `${LocNameStart}NotificationSetPreferredSuccess`
	| `${LocNameStart}NotificationDeleteEmailInstructions`
	| `${LocNameStart}ButtonSetPreferred`
	| `${LocNameStart}ButtonDeleteId`
	| `${LocNameStart}ContentDeletePreferredInfo`
	| `${LocNameStart}ContentDeleteInBadges`
	| `${LocNameStart}LabelCmcId`
	| `${LocNameStart}LabelFirstName`
	| `${LocNameStart}LabelLastName`
	| `${LocNameStart}LabelEmail`
	| `${LocNameStart}LabelPreferred`
	| `${LocNameStart}LabelCertmetricsId`;

export const spCredlyManagementLocs: LocDefs<SPCredlyManagementLocNames> = {
	SPCredlyManagementTitlePageSection: {
		defaultValue: 'Manage Candidate Credly ID',
		description: 'Page Section Title',
		displayPath: `${pathStart}Page Section Title`,
		displayType: 'Title',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.PageSectionTitle.CandidateEmails',
		...sharedProperties,
	},
	SPCredlyManagementNotificationDeleteSuccess: {
		defaultValue: 'Candidate External Id was succesfully deleted',
		description: 'Candidate external id was successfully deleted notification message',
		displayPath: `${pathStart}Delete Success`,
		displayType: 'Notification',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Notification.DeleteSuccess',
		...sharedProperties,
	},
	SPCredlyManagementNotificationDeleteFailure: {
		defaultValue: 'Something went wrong, please contact support',
		description: 'Candidate external id failed to delete notification message',
		displayPath: `${pathStart}Delete Failure`,
		displayType: 'Notification',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Notification.DeleteFailure',
		...sharedProperties,
	},
	SPCredlyManagementNotificationSetPreferredSuccess: {
		defaultValue: 'Set preferred successful',
		description: 'Set preferred successful notification message',
		displayPath: `${pathStart}Set Preferred Success`,
		displayType: 'Notification',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Notification.SetPreferredSuccessful',
		...sharedProperties,
	},
	SPCredlyManagementNotificationDeleteEmailInstructions: {
		defaultValue: `You may only delete emails that do not have any pending, accepted or rejected badges at
		Credly. If you want to remove an old email, you would need to contact the client or credly
		and have them delete the badges first. Note: this will cause the candidate to get new badges
		under whatever their current email is in demographics. If they had shared those badges under
		the old email, they would no longer be valid.`,
		description: 'Delete email instructions notification message',
		displayPath: `${pathStart}Delete email instructions`,
		displayType: 'Notification',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Notification.DeleteEmailInstructions',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ShareCredlyBadging,
		portal: 'SP',
	},
	SPCredlyManagementButtonSetPreferred: {
		defaultValue: 'Set Preferred',
		description: 'Set Preferred button label',
		displayPath: `${pathStart}Set Preferred`,
		displayType: 'Button',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Button.SetPreferred',
		...sharedProperties,
	},
	SPCredlyManagementButtonDeleteId: {
		defaultValue: 'Delete External Id',
		description: 'Delete External Id button label',
		displayPath: `${pathStart}Delete External Id`,
		displayType: 'Button',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Button.DeleteId',
		...sharedProperties,
	},
	SPCredlyManagementContentDeletePreferredInfo: {
		defaultValue:
			'This is the preferred email of the candidate with Credly. It cannot be deleted, unless a different email is set as a preferred email.',
		description: 'Delete preferred email content',
		displayPath: `${pathStart}Delete preferred email`,
		displayType: 'Content',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Content.DeletePreferredInfo',
		...sharedProperties,
	},
	SPCredlyManagementContentDeleteInBadges: {
		defaultValue:
			'You may only delete emails that do not have any pending, accepted or rejected badges at Credly. If you want to remove an old email, you would need to contact the client or Credly and have them delete the badges first.',
		description: 'Delete in badges content',
		displayPath: `${pathStart}Delete in badges`,
		displayType: 'Content',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Content.DeleteInBadges',
		...sharedProperties,
	},
	SPCredlyManagementLabelCmcId: {
		defaultValue: 'CMC ID',
		description: 'Column heading for: CMC ID.',
		displayPath: `${pathStart}CMC ID`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.ColumnHeading.CmcId',
		...sharedProperties,
	},
	SPCredlyManagementLabelFirstName: {
		defaultValue: 'First Name',
		description: 'Column heading for: First Name.',
		displayPath: `${pathStart}First Name`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.ColumnHeading.FirstName',
		...sharedProperties,
	},
	SPCredlyManagementLabelLastName: {
		defaultValue: 'Last Name',
		description: 'Column heading for: Last Name.',
		displayPath: `${pathStart}Last Name`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.ColumnHeading.LastName',
		...sharedProperties,
	},
	SPCredlyManagementLabelEmail: {
		defaultValue: 'Email',
		description: 'Column heading for: Email.',
		displayPath: `${pathStart}Email`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.ColumnHeading.DuplicateEmail',
		...sharedProperties,
	},
	SPCredlyManagementLabelPreferred: {
		defaultValue: 'Preferred',
		description: 'Column heading for: Preferred.',
		displayPath: `${pathStart}Preferred`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.ColumnHeading.Preferred',
		...sharedProperties,
	},
	SPCredlyManagementLabelCertmetricsId: {
		defaultValue: 'CertMetrics ID',
		description: 'Column heading for: CertMetrics ID.',
		displayPath: `${pathStart}CertMetrics ID`,
		displayType: 'Label',
		...sharedProperties,
	},
};

export type SPCredlyManagementLocType = LocMap<SPCredlyManagementLocNames>;
